import DRAWER_MODES from 'app/playbook-editor/transform-drawer/TestPlaybookDrawerContextProvider/DRAWER_MODES';
import useTestPlaybookTabs, { TEST_PLAYBOOK_TABS } from './useTestPlaybookTabs';
import usePipelineStageOutput from './usePipelineStageOutput';
import usePlaybookStageOutput from './usePlaybookStageOutput';
import buildDeliverIntegration from './buildDeliverIntegration';
import useEditorServerResponse from './useEditorServerResponse';
import buildChainDisabledTabs from './buildChainDisabledTabs';

const IDLE_DELIVER_BUILD = { integration: null, buildError: null };

export default function useTestPlaybookChainState({
  mode,
  parsedInput,
  hasValidInput,
  clusterId,
  editorPlaybook,
  connectionJson,
  integration,
  revisionMeta,
  pipelineSelected,
  deployedPipeline,
  attachment,
  pipelineDataLoading,
}) {
  const [selectedTab, { onSelectTab }] = useTestPlaybookTabs();
  const [runPipelineTransform, pipelineOutput] = usePipelineStageOutput({ deployedPipeline, clusterId });
  const [runPlaybookTransform, playbookOutput] = usePlaybookStageOutput({
    editorPlaybook,
    revisionMeta,
    attachment,
    clusterId,
  });
  // The build clones + translates the whole connection on every render, so it
  // only runs in Advanced mode — Basic mode never reads its result.
  const deliverBuild =
    mode === DRAWER_MODES.ADVANCED
      ? buildDeliverIntegration({
          connectionJson,
          revisionMeta,
          attachment,
          starterkitId: integration.starterkitId,
          integrationId: integration.id,
        })
      : IDLE_DELIVER_BUILD;
  const serverResponse = useEditorServerResponse({
    clusterId,
    deliverIntegration: deliverBuild.integration,
    playbookOutput,
    originalEvent: parsedInput,
  });

  // A cluster/pipeline/input change invalidates stage 2 onward, so the whole
  // chain resets to the Input tab (unlike the pipeline page, which keeps stage 1).
  const resetChain = () => {
    pipelineOutput.reset();
    playbookOutput.reset();
    serverResponse.reset();
    onSelectTab(TEST_PLAYBOOK_TABS.INPUT_EVENT);
  };

  const disabledTabs = buildChainDisabledTabs({
    hasValidInput,
    clusterId,
    pipelineSelected,
    pipelineDataLoading,
    pipelineOutput,
    playbookOutput,
    deliverBuildError: deliverBuild.buildError,
  });

  return {
    selectedTab,
    onSelectTab,
    resetChain,
    executePipelineTransform: () => runPipelineTransform({ parsedInput }),
    executePlaybookTransform: () =>
      runPlaybookTransform({ inputEvent: pipelineOutput.outputEvent, originalEvent: parsedInput }),
    pipelineOutput,
    playbookOutput,
    serverResponse,
    deliverBuild,
    ...disabledTabs,
  };
}
